import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getBasicFilteringOptions,
  getClearBasicModel,
} from "../../common/EntitiesManagement";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function Position() {
  const {
    positionModel,
    setPositionModel,
    positionData,
    POSITION_ENDPOINT,
    config,
    pageIndexPosition,
    setPageIndexPosition,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [positionDataFiltered, setPositionDataFiltered] = useState(
    positionData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const positionDataObj = positionData.Data.find(
        (positionObj) => positionObj.Id === Number(id)
      );
      setPositionModel(positionDataObj);
    }
  }, [id]);

  useEffect(() => {
    setPositionDataFiltered(positionData.Data);
  }, [positionData]);

  const validateOperationResult = (result, isDelete) => {
    setPositionModel(getClearBasicModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([POSITION_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexPosition !== 0 && positionModel.Data.length === 1) {
        setPageIndexPosition(pageIndexPosition - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${POSITION_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${POSITION_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${POSITION_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !positionModel?.Name || positionModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.positions}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={POSITION_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="position"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_position}
          isEditForm
          onClickToSubmit={() => {
            handleUpdateRequest(positionModel);
            setShowEditModal(false);
            setPositionModel(getClearBasicModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setPositionModel(getClearBasicModel());
          }}
          Component={
            <AddOrEditBasicEntity
              customWidth="100%"
              nameMandatory
              model={positionModel}
              setModel={setPositionModel}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.positions}
            subtitle={localize.position_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="position"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_position}
                onClickToSubmit={() => {
                  handleCreateRequest(positionModel);
                  setShowAddModal(false);
                  setPositionModel(getClearBasicModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setPositionModel(getClearBasicModel());
                }}
                Component={
                  <AddOrEditBasicEntity
                    customWidth="100%"
                    nameMandatory
                    model={positionModel}
                    setModel={setPositionModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={positionModel.Name}
        entityType={localize.position}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setPositionModel(getClearBasicModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(positionModel.Id);
          setPositionModel(getClearBasicModel());
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexPosition}
            setPageIndex={setPageIndexPosition}
            dataArray={positionData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          positionData.Data?.length !== 0
            ? positionDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setPositionModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setPositionModel(item);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(5)]
        }
      />
    </div>
  );
}
